// FLAGS: health-data
// Assembles the expert's own sessions page: their 1:1 consults plus the private spaces they host,
// as one chronological, paginated list. Owns both requests so the view stays wiring only.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS, pageSize } from "@src/constants";
import { useFeatureFlagQuery } from "@src/queries/featureflags";
import { SessionsFilter } from "@src/components/SessionFilters/SessionFilters";
import { useExpertsConsultsQuery } from "@src/queries/experts";
import {
  useHostedSpacesQuery,
  useSpaceStartLinkMutation,
} from "@src/queries/spaces";
import { toIsoString } from "@src/utils/formatter";
import {
  getSpaceStatusFacet,
  mapHostedSessionToSpaceRow,
} from "@src/views/MySessions/spaceRow";
import { SessionsViewRow } from "@src/views/SessionsView";

// An expert hosts few private spaces, so the whole set fits in one request and is held here. That
// is what makes the merge exact rather than approximate: producing merged rows [0, K) needs the
// first K rows of each source, and with every space already in hand only the consults have to grow
// with the page. Fetching a fixed window from both instead would silently truncate the expert's
// consult history to that window, which is how an earlier revision of this page capped a long
// history at 50 rows.
const SPACES_FETCH_CAP = 200;

const startsAtOf = (row: SessionsViewRow) =>
  row.kind === "consult" ? row.consult.startedAt : row.space.startsAt;

export const useMySessionsRows = (filters: SessionsFilter, page: number) => {
  const { t } = useTranslation();

  const spaceStatusFacet = getSpaceStatusFacet(filters.status);
  const includeSpaces = spaceStatusFacet !== undefined;

  const {
    data: {
      data: consults = [],
      pagination: { total: consultsTotal = 0, hasNextPage = false } = {},
    } = {},
    isFetching,
  } = useExpertsConsultsQuery({
    // While spaces are merged in, the consults are re-fetched cumulatively so this hook holds
    // every consult that could fall on or before the current page. Without spaces the table shows
    // one server page, exactly as it did before.
    page: includeSpaces ? 0 : page,
    limit: includeSpaces ? (page + 1) * pageSize : undefined,
    search: filters.searchTerm,
    status: filters.status,
    // Axios serializes a Date param with toISOString anyway, so this is the same value on the
    // wire; it is spelled out because the param is typed as a string, and because a filter
    // restored from localStorage arrives as a string that is only typed as a Date.
    startedAtFrom: toIsoString(filters.startedAtFrom),
    startedAtTo: toIsoString(filters.startedAtTo),
    employerId: filters.employer?.id,
  });

  const { data: spacesData, isLoading: isLoadingSpaces } = useHostedSpacesQuery(
    {
      page: 0,
      limit: SPACES_FETCH_CAP,
      status: spaceStatusFacet,
      employerId: filters.employer?.id,
      startedAtFrom: toIsoString(filters.startedAtFrom),
      startedAtTo: toIsoString(filters.startedAtTo),
      search: filters.searchTerm || undefined,
    },
    includeSpaces,
  );

  // Withdraw as host belongs to the Spaces Schedules flow, so it is offered only while that page's
  // flag is on (OI-26005); placeholder false means it never flashes in before the flag is read.
  const { data: spacesSchedulesEnabled = false } = useFeatureFlagQuery(
    FEATURE_FLAGS.ADMIN_SPACES_SCHEDULES,
  );

  const spaceRows = useMemo(
    () =>
      includeSpaces
        ? (spacesData?.data ?? []).map((session) => {
            const row = mapHostedSessionToSpaceRow(session);
            return spacesSchedulesEnabled
              ? row
              : { ...row, withdrawAsHost: undefined };
          })
        : [],
    [includeSpaces, spacesData, spacesSchedulesEnabled],
  );

  const [joinError, setJoinError] = useState<string | null>(null);
  const startLinkMutation = useSpaceStartLinkMutation();

  // A private space has no stored join URL by design: the start link embeds a short-lived Zoom
  // token, so it is requested per click and never persisted. The tab is opened synchronously,
  // before the await, because popup blockers reject a window opened from an async continuation.
  const joinSpace = async (sessionId: string) => {
    setJoinError(null);
    const startWindow = window.open("", "_blank");
    if (startWindow) {
      startWindow.opener = null;
    }

    try {
      const { startUrl } = await startLinkMutation.mutateAsync(sessionId);
      if (startWindow) {
        startWindow.location.href = startUrl;
      }
    } catch {
      startWindow?.close();
      setJoinError(t("PrivateSpaces.StartError"));
    }
  };

  // Mirrors what each backend does for its own facet: the scheduled list answers "what is next",
  // so it runs soonest first, while completed and all answer "what happened", so they run most
  // recent first. Sorting everything ascending would open My Sessions on the expert's oldest
  // session. `startedAt` and `startsAt` are ISO-8601, so lexical order is chronological order.
  const soonestFirst = spaceStatusFacet === "scheduled";

  const { rows, total } = useMemo(() => {
    const consultRows = consults.map(
      (consult): SessionsViewRow => ({ kind: "consult", consult }),
    );

    if (!includeSpaces) {
      return { rows: consultRows, total: consultsTotal };
    }

    const merged = [
      ...consultRows,
      ...spaceRows.map((space): SessionsViewRow => ({ kind: "space", space })),
    ].sort((a, b) =>
      soonestFirst
        ? startsAtOf(a).localeCompare(startsAtOf(b))
        : startsAtOf(b).localeCompare(startsAtOf(a)),
    );

    const start = page * pageSize;
    return {
      rows: merged.slice(start, start + pageSize),
      total: consultsTotal + spaceRows.length,
    };
  }, [consults, consultsTotal, includeSpaces, page, soonestFirst, spaceRows]);

  return {
    rows,
    total,
    hasNextPage: includeSpaces ? (page + 1) * pageSize < total : hasNextPage,
    isFetching: isFetching || isLoadingSpaces,
    spaceJoin: {
      onJoin: joinSpace,
      isJoining: startLinkMutation.isPending,
      error: joinError,
    },
  };
};
